import { FORM_STATUS_ENUM } from "@interfaces/response";
import { Button, useMediaQuery, useTheme } from "@mui/material";
import { getExpedienteButtonColors } from "@theme/_themeBase";

export type ExpedienteButtonMode = "form" | "validation";
export type ExpedienteVisualStatus = "pending" | "complete" | "validated" | "incident";

interface ButtonVisualParams {
  disabled: boolean;
  buttonLabel: string;
  backgroundColor: string;
  color: string;
  hoverBg: string;
  hoverText: string;
}

const DISABLED_BY_STATUS: Record<ExpedienteVisualStatus, boolean> = {
  pending: false,
  validated: true,
  complete: true,
  incident: false,
};

const LABELS: Record<ExpedienteButtonMode, Record<ExpedienteVisualStatus, string>> = {
  form: {
    pending: "Llenar",
    validated: "Validado",
    complete: "Completo",
    incident: "Editar",
  },
  validation: {
    pending: "Validar",
    validated: "Validado",
    complete: "Completo",
    incident: "Volver a validar",
  },
};

const fromFormStatus = (formStatus: FORM_STATUS_ENUM | null | undefined): ExpedienteVisualStatus => {
  if (formStatus === "VALIDADO") return "validated";
  if (formStatus === "COMPLETO") return "complete";
  if (formStatus === "INCIDENCIA") return "incident";
  return "pending";
};

const fromValidation = (
  isCompleted?: boolean | null,
  documentStatus?: boolean | null,
): ExpedienteVisualStatus => {
  if (isCompleted === true && documentStatus === true) return "validated";
  if (isCompleted === true) return "complete";
  if (isCompleted === false) return "incident";
  return "pending";
};

type ButtonExpedienteStatusFormProps = {
  mode: "form";
  formStatus?: FORM_STATUS_ENUM | null;
  isCompleted?: never;
  documentStatus?: never;
};

type ButtonExpedienteStatusValidationProps = {
  mode: "validation";
  formStatus?: never;
  isCompleted?: boolean | null;
  documentStatus?: boolean | null;
};

export type ButtonExpedienteStatusProps = {
  fakeDisabled?: boolean;
  onClick?: () => void;
} & (ButtonExpedienteStatusFormProps | ButtonExpedienteStatusValidationProps);

const ButtonExpedienteStatus: React.FC<ButtonExpedienteStatusProps> = (props) => {
  const { fakeDisabled, onClick, mode } = props;
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const colors = getExpedienteButtonColors(theme);

  const status =
    mode === "form" ? fromFormStatus(props.formStatus) : fromValidation(props.isCompleted, props.documentStatus);

  const statusColors = colors[status];
  const btnParams: ButtonVisualParams = {
    disabled: DISABLED_BY_STATUS[status],
    buttonLabel: LABELS[mode][status],
    backgroundColor: statusColors.bg,
    color: statusColors.text,
    hoverBg: statusColors.hoverBg ?? statusColors.bg,
    hoverText: statusColors.hoverText ?? statusColors.text,
  };

  return (
    <Button
      variant="expediente"
      size={isMobileOrTablet ? "small" : "medium"}
      onClick={onClick}
      disabled={btnParams.disabled}
      sx={{
        backgroundColor: btnParams.backgroundColor,
        color: btnParams.color,
        opacity: fakeDisabled ? 0.5 : 1,
        ...(isMobileOrTablet
          ? {
              width: "auto",
              maxWidth: "100%",
              height: "auto",
              minHeight: "23px",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              textAlign: "center",
              lineHeight: 1.15,
              paddingTop: "6px",
              paddingBottom: "6px",
            }
          : null),
        "&:hover": {
          backgroundColor: btnParams.hoverBg,
          color: btnParams.hoverText,
        },
        "&.Mui-disabled": {
          backgroundColor: btnParams.backgroundColor,
          color: btnParams.color,
          opacity: 1,
        },
      }}
    >
      {btnParams.buttonLabel}
    </Button>
  );
};

export default ButtonExpedienteStatus;
